import { z } from 'zod';

import { normalizeGender, normalizeSex } from '@components/Patient/patientValidation';

import type { PatientInfoResponse } from '../../store/api';

// --- Reusable field validators ---

const optionalPhone = z
  .string()
  .refine(val => !val || /^\+?[\d\s\-().]{7,20}$/.test(val), {
    message: 'Please enter a valid phone number',
  })
  .nullish();

const optionalEmail = z
  .string()
  .email('Please enter a valid email')
  .or(z.literal(''))
  .nullish();

const optionalDob = z
  .string()
  .refine(val => !val || !isNaN(new Date(val).getTime()), {
    message: 'Please enter a valid date',
  })
  .refine(val => !val || new Date(val) <= new Date(), {
    message: 'Date of birth cannot be in the future',
  })
  .refine(val => !val || new Date(val) >= new Date('1900-01-01'), {
    message: 'Please enter a valid date of birth',
  })
  .nullish();

// Vitals (height in inches, weight in pounds). Stored as a number or null;
// the field control coerces the empty input to null and the typed input to a
// number, so validation only has to reject non-positive values.
const optionalPositiveNumber = z
  .number()
  .positive('Must be greater than 0')
  .nullish();

// --- Address schema ---

const addressSchema = z
  .object({
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z
      .string()
      .max(2, 'State must be a 2-letter abbreviation')
      .nullish(),
    zip: z
      .string()
      .refine(val => !val || /^\d{5}(-\d{4})?$/.test(val), {
        message: 'ZIP must be 5 or 9 digits (e.g., 12345 or 12345-6789)',
      })
      .nullish(),
  })
  .nullish();

// --- Emergency contact schema ---

const emergencyContactSchema = z
  .object({
    name: z.string().nullish(),
    relationship: z.string().nullish(),
    phone: optionalPhone,
    email: optionalEmail,
  })
  .nullish();

// --- Main form schema ---

export const patientInfoSchema = z.object({
  // Demographics
  first_name: z.string().nullish(),
  middle_name: z.string().nullish(),
  last_name: z.string().nullish(),
  preferred_name: z.string().nullish(),
  identifier: z.string().nullish(),
  dob: optionalDob,
  gender: z.string().nullish(),
  sex: z.string().nullish(),
  pronouns: z.string().nullish(),
  race: z.string().nullish(),

  // Vitals (required by DoseSpot to e-prescribe for minors)
  height: optionalPositiveNumber,
  weight: optionalPositiveNumber,

  // Contact
  mobile_phone: optionalPhone,
  email: optionalEmail,

  // Addresses
  primary_address: addressSchema,
  secondary_address: addressSchema,

  // Emergency contact
  emergency_contact: emergencyContactSchema,

  // Employment
  employment_status: z.string().nullish(),
  occupation: z.string().nullish(),

  // Consent
  patient_consent_on_file: z.boolean().nullish(),
});

export type PatientInfoFormData = z.infer<typeof patientInfoSchema>;

// --- Default values ---

export const EMPTY_FORM_VALUES: PatientInfoFormData = {
  first_name: '',
  middle_name: '',
  last_name: '',
  preferred_name: '',
  identifier: '',
  dob: '',
  gender: '',
  sex: '',
  pronouns: '',
  race: '',
  height: null,
  weight: null,
  mobile_phone: '',
  email: '',
  primary_address: { line1: '', line2: '', city: '', state: '', zip: '' },
  secondary_address: null,
  emergency_contact: { name: '', relationship: '', phone: '', email: '' },
  employment_status: '',
  occupation: '',
  patient_consent_on_file: false,
};

// --- Transform API response → form data ---

export function transformResponseToFormData(
  response: PatientInfoResponse,
): PatientInfoFormData {
  const { demographics, contact, addresses, emergency_contact, employment, consent } = response;

  return {
    first_name: demographics.first_name ?? '',
    middle_name: demographics.middle_name ?? '',
    last_name: demographics.last_name ?? '',
    preferred_name: demographics.preferred_name ?? '',
    identifier: demographics.identifier ?? '',
    dob: demographics.dob ?? '',
    // Normalize legacy casings (e.g. 'Male' -> 'male') so the value matches a
    // canonical select option and renders selected rather than blank.
    gender: normalizeGender(demographics.gender ?? '') as string,
    sex: normalizeSex(demographics.sex ?? '') as string,
    pronouns: demographics.pronouns ?? '',
    race: demographics.race ?? '',
    height: demographics.height ?? null,
    weight: demographics.weight ?? null,

    mobile_phone: contact.mobile_phone ?? '',
    email: contact.email ?? '',

    primary_address: addresses.primary_address
      ? {
          line1: addresses.primary_address.line1 ?? '',
          line2: addresses.primary_address.line2 ?? '',
          city: addresses.primary_address.city ?? '',
          state: addresses.primary_address.state ?? '',
          zip: addresses.primary_address.zip ?? '',
        }
      : { line1: '', line2: '', city: '', state: '', zip: '' },

    secondary_address: addresses.secondary_address
      ? {
          line1: addresses.secondary_address.line1 ?? '',
          line2: addresses.secondary_address.line2 ?? '',
          city: addresses.secondary_address.city ?? '',
          state: addresses.secondary_address.state ?? '',
          zip: addresses.secondary_address.zip ?? '',
        }
      : null,

    emergency_contact: emergency_contact
      ? {
          name: emergency_contact.name ?? '',
          relationship: emergency_contact.relationship ?? '',
          phone: emergency_contact.phone ?? '',
          email: emergency_contact.email ?? '',
        }
      : { name: '', relationship: '', phone: '', email: '' },

    employment_status: employment.employment_status ?? '',
    occupation: employment.occupation ?? '',
    patient_consent_on_file: consent.patient_consent_on_file ?? false,
  };
}
